import axios from 'axios';

// API基础URL配置
// - 若 REACT_APP_API_URL 为空：使用同源请求，由 Nginx 代理 /api 到后端
// - 若 REACT_APP_API_URL 有值：使用指定地址
// - 开发环境默认 localhost:8000
const envUrl = process.env.REACT_APP_API_URL;
export const API_BASE_URL = (envUrl && envUrl.trim() !== '')
  ? envUrl
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理 401 错误（登录接口的 401 不跳转，仅提示错误）
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 翻译错误信息
    let errorMsg = '请求失败';
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail;
      
      if (status === 401) {
        errorMsg = '登录已过期或未授权';
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        if (!isLoginRequest) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('username');
          window.location.href = '/';
        }
      } else if (status === 403) {
        errorMsg = '没有权限执行此操作';
      } else if (status === 404) {
        errorMsg = '请求的资源不存在';
      } else if (status === 405) {
        errorMsg = '请求方法不允许 (Method Not Allowed)';
      } else if (status === 500) {
        errorMsg = detail || '服务器内部错误';
      } else if (detail) {
        errorMsg = detail; // 如果后端返回了具体错误信息，优先使用
      }
      
      // 覆盖 response data detail 以便下游组件显示中文
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data.detail = errorMsg;
      }
    } else if (error.request) {
      // 网络错误
      errorMsg = '网络连接失败，请检查网络或后端服务是否启动';
      error.message = errorMsg;
    } else {
      errorMsg = error.message;
    }

    // 可以选择在这里统一提示，或者修改 error 对象让组件提示
    // 为了不破坏现有组件的 try-catch 逻辑，我们主要修改 error 对象
    // 但对于 500/405 等严重错误，组件可能处理不好，这里可以加个 console log
    console.error('API Error:', errorMsg);

    return Promise.reject(error);
  }
);

export interface Agent {
  id: number;
  name: string;
  root_advertiser_id: string;
  app_id: string;
  has_token: boolean;
  advertiser_count: number;
}

export interface Advertiser {
  id: number;
  advertiser_id: string;
  uuid?: string;
  name: string;
  company_name?: string;
  industry: string;
  qual_status: number | null;
  agent_id?: number;
}

export interface Qualification {
  id: number;
  advertiser_id: number;
  advertiser_uuid?: string; // New

  advertiser_name: string;
  company_name?: string; // New
  qual_id: string;
  product_name?: string; // New
  product_type?: number; // New
  industry_first: string;
  industry_first_name?: string; // New
  industry_second: string;
  industry_second_name?: string; // New
  description: string;
  state: number;
  state_text: string;
  valid_date: string;
  image_urls: string[];
  reject_reason: string | null;
}

export interface Industry {
  id: string;
  name: string;
  parent_id: string | null;
  qualifications?: {
    industry: string;
    necessary: string;
    supplementary: string;
  };
}

// 代理商相关
export const getAgents = () => api.get<Agent[]>(`/api/agents`);

export const createAgent = (data: { name: string; root_advertiser_id?: string }) =>
  api.post<Agent>(`/api/agents`, data);

export const updateAgent = (id: number, data: { name?: string; root_advertiser_id?: string }) =>
  api.put<Agent>(`/api/agents/${id}`, data);

export const deleteAgent = (id: number) =>
  api.delete(`/api/agents/${id}`);

export const batchDeleteAgents = (agentIds: number[]) =>
  api.post<{ success: boolean; message: string }>(`/api/agents/batch_delete`, { agent_ids: agentIds });

export const importAgents = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>(`/api/agents/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getAuthUrl = (agentId: number) =>
  api.get<{ authorization_url: string; agent_id: number; agent_name: string }>(
    `/api/auth/url/${agentId}`
  );

export const getAgent = (agentId: number) => 
  api.get<Agent>(`/api/agents/${agentId}`);

export const getAdvertisers = (agentId: number, skip = 0, limit = 10000) =>
  api.get<Advertiser[]>(`/api/agents/${agentId}/advertisers`, {
    params: { skip, limit }
  });

export const getAdvertisersByAgents = (agentIds: number[], page = 1, pageSize = 10000) =>
  api.post<{
    total: number;
    items: Advertiser[];
  }>(`/api/agents/advertisers/list`, {
    agent_ids: agentIds,
    page,
    page_size: pageSize
  });

export const addAdvertiser = (agentId: number, data: {
  advertiser_id: string;
  name: string;
  industry?: string;
}) => api.post(`/api/agents/${agentId}/advertisers`, data);

export const importAdvertisers = (agentId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>(`/api/agents/${agentId}/advertisers/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const syncAdvertisers = (agentId: number) =>
  api.post<{
    success: boolean;
    synced_count: number;
    message: string;
    task_id: string;
  }>(`/api/agents/${agentId}/advertisers/sync`);

// 资质相关
export const getQualList = (
  advertiserIds: number[], 
  page = 1, 
  pageSize = 20, 
  statusFilter: number[] | null = null,
  industryFirst?: string,
  industrySecond?: string,
  productName?: string
) =>
  api.post<{
    total: number;
    page: number;
    page_size: number;
    data: Qualification[];
  }>('/api/qual/list', {
    advertiser_ids: advertiserIds,
    page,
    page_size: pageSize,
    status_filter: statusFilter,
    industry_first: industryFirst,
    industry_second: industrySecond,
    product_name: productName
  });

export const syncQualifications = (advertiserIds: number[]) =>
  api.post('/api/qual/sync', { advertiser_ids: advertiserIds });

export const uploadQualFile = (agentId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    file_id: string;
    file_url: string;
    filename: string;
  }>(`/api/qual/upload?agent_id=${agentId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const addQualification = (data: {
  advertiser_id: number;
  industry_first: string;
  industry_second: string;
  product_name: string;
  product_type: number;
  description: string;
  qualifications: { url: string; type_id: number; type: number; name?: string }[];
  icp_case_number?: string;
  icp_domain_name?: string;
}) => api.post('/api/qual/add', data);

export const batchAddQualification = (data: {
  advertiser_ids: number[];
  industry_first: string;
  industry_second: string;
  product_name: string;
  product_type: number;
  description: string;
  qualifications: { url: string; type_id: number; type: number; name?: string }[];
  icp_case_number?: string;
  icp_domain_name?: string;
}) => api.post('/api/qual/batch_add', data);

export const batchDeleteQualifications = (qualificationIds: number[]) =>
  api.post<{
    success: boolean;
    success_count: number;
    failed_count: number;
    errors: string[];
  }>('/api/qual/batch_delete', { qualification_ids: qualificationIds });

export const batchEditQualifications = (data: {
  advertiser_id: number;
  qualifications: {
      qual_id: string; // Vivo ID
      product_name?: string;
      product_type?: number;
      description?: string;
      qualifications?: { url: string; type_id: number; type: number; name?: string }[];
      icp_case_number?: string;
      icp_domain_name?: string;
  }[];
}) => api.post('/api/qual/batch_edit', data);

export const getIndustries = (agentId: number, parentId?: string, advertiserId?: string) =>
  api.get<{
    success: boolean;
    industries: Industry[];
  }>('/api/qual/industries', {
    params: { agent_id: agentId, parent_id: parentId, advertiser_id: advertiserId }
  });

// 认证（与 api/auth 保持一致，便于从 api 统一导入）
export { login } from './auth';

export interface TaskProgress {
  id: number;
  task_id: string;
  task_type: string;
  status: string;
  progress: number;
  message: string;
  result: string | null;
  created_at: string;
  updated_at: string;
}

export const getTasks = (skip: number = 0, limit: number = 20, type?: string) => {
  return api.get<TaskProgress[]>('/api/tasks', { params: { skip, limit, type } });
};

export const getTask = (taskId: string) => {
  return api.get<TaskProgress>(`/api/tasks/${taskId}`);
};

export const stopTask = (taskId: string) => {
  return api.post<{ success: boolean; message: string }>(`/api/tasks/${taskId}/stop`);
};

export const cleanupTasks = (days: number = 7) => {
  return api.post<{ success: boolean; message: string; deleted_count: number }>('/api/tasks/cleanup', null, {
    params: { days }
  });
};

export interface QualTemplate {
  id: number;
  name: string;
  qual_type: number;
  files: { type: number; name: string; files: { url: string; name: string; file_id?: string }[] }[];
  description: string;
  agent_id?: number;
  created_at: string;
}

export const addTemplate = (data: {
  name: string;
  qual_type: number;
  files: { type: number; name: string; files: { url: string; name: string; file_id?: string }[] }[];
  description?: string;
  agent_id?: number;
}) => api.post<QualTemplate>('/api/template/add', data);

export const getTemplates = (qual_type?: number, keyword?: string, agent_id?: number) =>
  api.get<QualTemplate[]>('/api/template/list', {
    params: { qual_type, keyword, agent_id }
  });

export const deleteTemplate = (id: number) =>
  api.delete<{ success: boolean; message: string }>(`/api/template/${id}`);


export default api;
